import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useAppSelector } from "@/app/appStore";
import {
  resetState,
  setSelectedOrder,
  selectAnnotationData,
  selectIsEditable,
  updateIsPlaybackLoaded,
} from "@/entities/annotation";
import { OrdersContainer } from "./orders-container";
import { AnnotationSidebar } from "./annotation-sidebar";
import { AnnotationEventDetails } from "./annotation-event-details";
import { EventDetailsWrapper } from "./event-details-wrapper";
import { useLazyOrderByIdQuery } from "@/shared/api/lod-api";
import { useUrlQueries } from "@/shared/hooks/useUrlQueries";
import { useTimelineData } from "../hooks/useTimelineData";
import { OrderWithJourneys } from "../lib/helpers";
import { usePlayer } from "@tezzro/player";

interface RightSidebarProps {
  setWorkspaceKey: (key: string) => void;
  onSelectOrder: (
    order: OrderWithJourneys,
    shouldUpdateCurrentTime?: boolean
  ) => void;
  onFinishAnnotation: () => void;
  timerEnabled: boolean;
  timerTimeLeft: number;
  onTimerEnable: (orderId: string) => void;
  onTimerDisable: (orderId: string) => void;
}

export const RightSidebar: React.FC<RightSidebarProps> = ({
  setWorkspaceKey,
  onSelectOrder,
  onFinishAnnotation,
  timerEnabled,
  timerTimeLeft,
  onTimerEnable,
  onTimerDisable,
}) => {
  const dispatch = useDispatch();
  const player = usePlayer();
  const { clearTimelineData } = useTimelineData();
  const { getAllQueries } = useUrlQueries();
  const navigate = useNavigate();
  const { storeKey = "", orderId: orderIdParam } = useParams<{
    storeKey?: string;
    orderId?: string;
  }>();

  const [getOrderById, { isFetching: isFetchingOrder }] =
    useLazyOrderByIdQuery();

  const selectedOrder = useAppSelector(
    (state) => state.annotation.selectedOrder
  );
  const selectedEvent = useAppSelector(
    (state) => state.annotation.selectedEvent
  );
  const annotationData = useAppSelector(selectAnnotationData);
  const isEditable = useAppSelector(selectIsEditable);

  const [highlightedOrderId, setHighlightedOrderId] = useState<string | null>(
    null
  );

  const handleCleanupAndNavigateBack = useCallback(
    (selectedOrderId?: string) => {
      if (selectedOrderId) {
        setHighlightedOrderId(selectedOrderId);
      }
      clearTimelineData();
      dispatch(setSelectedOrder(null));
      dispatch(resetState());
      setWorkspaceKey(crypto.randomUUID());
      dispatch(updateIsPlaybackLoaded(false));
      player.reset();
    },
    [clearTimelineData, dispatch, setWorkspaceKey, player]
  );

  const loadSingleOrderData = useCallback(
    async (orderId: string, shouldUpdateCurrentTime?: boolean) => {
      if (isFetchingOrder) {
        return;
      }

      const { data: response } = await getOrderById(orderId);
      const order = response?.result;

      if (order) {
        onSelectOrder(order, shouldUpdateCurrentTime);
      } else {
        const currentQueries = getAllQueries();
        const queryString = new URLSearchParams(currentQueries).toString();

        navigate({
          pathname: `/stores/${storeKey}`,
          search: queryString ? `?${queryString}` : "",
        });
      }
    },
    [
      getOrderById,
      onSelectOrder,
      getAllQueries,
      navigate,
      storeKey,
      isFetchingOrder,
    ]
  );

  useEffect(() => {
    const selectedOrderId = selectedOrder?.id;
    const hasSelectedOrder = !!selectedOrder;

    if (orderIdParam && selectedOrderId !== orderIdParam) {
      loadSingleOrderData(orderIdParam, true);
    } else if (!orderIdParam && hasSelectedOrder) {
      handleCleanupAndNavigateBack(selectedOrderId);
    }
  }, [
    orderIdParam,
    selectedOrder,
    loadSingleOrderData,
    handleCleanupAndNavigateBack,
    isFetchingOrder,
  ]);

  if (annotationData && isEditable) {
    return <AnnotationSidebar />;
  }

  if (selectedEvent) {
    return (
      <EventDetailsWrapper>
        <AnnotationEventDetails />
      </EventDetailsWrapper>
    );
  }

  return (
    <OrdersContainer
      isOrderLoading={isFetchingOrder}
      onSelectOrderById={loadSingleOrderData}
      onFinishAnnotation={onFinishAnnotation}
      timerEnabled={timerEnabled}
      timerTimeLeft={timerTimeLeft}
      onTimerEnable={onTimerEnable}
      onTimerDisable={onTimerDisable}
      highlightedOrderId={highlightedOrderId}
      onCleanupAndNavigateBack={handleCleanupAndNavigateBack}
    />
  );
};
